import { Check } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { Surface } from '@/components/ui/surface'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { toast } from '@/lib/toast'
import { toastFailure } from '@/lib/feedback'
import { CrmApiError } from '../http'
import { weekdayDay } from '../record/format'
import { undoCapture } from './api'
import { ACTIVITY_LABEL, undoUntilText } from './reviewModel'
import { relationPath } from './relation'
import { captureContext, type CaptureContext, type CreatedRefs, type VoiceCapture } from './types'
import { useRelationName } from './useRelationName'

function Line({ to, icon, children }: { to: string; icon: string; children: ReactNode }) {
  return (
    <li>
      <Link to={to} className="flex min-h-11 items-center gap-2 text-base hover:underline">
        <span aria-hidden className="w-5 shrink-0 text-center text-muted-foreground">
          {icon}
        </span>
        <span className="min-w-0 flex-1">{children}</span>
        <span aria-hidden>→</span>
      </Link>
    </li>
  )
}

/**
 * "✓ Vastgelegd" (addendum B.4.4): one linked line per record, [Ongedaan maken] while it is
 * allowed (24 hours, only when nothing was changed since), [Nog iets inspreken] and [Naar relatie].
 */
export function ResultScreen({
  capture,
  onUndone,
}: {
  capture: VoiceCapture
  onUndone: (capture: VoiceCapture) => void
}) {
  const { membersById } = useTeamMembers()
  const [busy, setBusy] = useState(false)
  const [now] = useState(() => Date.now())
  const [conflicts, setConflicts] = useState<string[]>([])
  const refs = (capture.created_refs ?? {}) as Partial<CreatedRefs>
  const undone = capture.status === 'ongedaan'
  const context: CaptureContext | null =
    captureContext(capture) ?? (refs.person_ids?.[0] ? { type: 'contact', id: refs.person_ids[0] } : null)
  const relationName = useRelationName(context)
  const undoUntil = capture.undo_until ?? null
  const canUndo = !undone && undoUntil !== null && new Date(undoUntil).getTime() > now
  const here = relationPath(context)
  const newHref = context ? `/relaties/inspreken?relatie=${context.type}:${context.id}` : '/relaties/inspreken'

  async function undo() {
    setBusy(true)
    setConflicts([])
    try {
      onUndone(await undoCapture(capture.id))
      toast.success('Ongedaan gemaakt.')
    } catch (caught) {
      if (caught instanceof CrmApiError && caught.code === 'undo_conflict') {
        const lines = caught.body?.conflicts
        setConflicts(Array.isArray(lines) ? lines.map(String) : [caught.message])
      } else {
        toastFailure(caught, 'Ongedaan maken mislukt.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-bold">
        {undone ? 'Ongedaan gemaakt' : (
          <>
            <Check aria-hidden className="size-6 text-green-700" />
            Vastgelegd
          </>
        )}
      </h1>
      {undone ? (
        <p className="text-base text-muted-foreground">Alles van dit verslag is teruggedraaid.</p>
      ) : (
        <ul className="space-y-1">
          {(refs.contacts ?? []).map((item) => (
            <Line key={item.ref} to={`/relaties/contacten/${item.id}`} icon="+">{`Nieuw contact ${item.name}`}</Line>
          ))}
          {(refs.activities ?? []).map((item) => (
            <Line key={item.ref} to={here} icon="☎">
              {`${ACTIVITY_LABEL[item.type]}${item.title ? ` '${item.title}'` : ''} · ${weekdayDay(item.occurred_on)}`}
            </Line>
          ))}
          {(refs.tasks ?? []).map((item) => {
            const owner = item.assignee_ids.map((id) => membersById?.get(id)?.display_name).find(Boolean)
            return (
              <Line key={item.ref} to="/taken" icon="○">
                {`Taak '${item.title}'${owner ? ` · ${owner}` : ''}${item.due_on ? ` · ${weekdayDay(item.due_on)}` : ''}`}
              </Line>
            )
          })}
          {(refs.agreement_changes ?? []).map((item) => (
            <Line key={item.ref} to="/relaties/afspraken" icon="↻">{`Afspraak '${item.title}' gewijzigd`}</Line>
          ))}
          {(refs.new_agreements ?? []).map((item) => (
            <Line key={item.ref} to="/relaties/afspraken" icon="↻">{`Nieuwe afspraak '${item.title}'`}</Line>
          ))}
        </ul>
      )}

      {conflicts.length > 0 ? (
        <Surface variant="inset" padding="sm" className="space-y-1" role="alert">
          <p className="text-base font-semibold">Dit kan niet meer automatisch ongedaan worden gemaakt.</p>
          <ul className="list-disc space-y-1 pl-5 text-base">
            {conflicts.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Surface>
      ) : null}

      <div className="flex flex-col gap-2">
        {!undone && canUndo && undoUntil ? (
          <div>
            <Button variant="outline" disabled={busy} onClick={() => void undo()}>
              Ongedaan maken
            </Button>
            <p className="pt-1 text-sm text-muted-foreground">{`kan tot ${undoUntilText(undoUntil)}`}</p>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to={newHref}>Nog iets inspreken</Link>
          </Button>
          {context ? (
            <Button asChild>
              <Link to={here}>{`Naar ${relationName ?? 'de relatie'}`}</Link>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
